import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Workflow, ArrowRight } from 'lucide-react';
import { TRAJECTORY_META } from './ProgramNavigationPanel';

/**
 * Value Pathways — the Missing Middle portfolio view. Ranks programs by
 * how effectively they convert capability into value (impact ÷ investment),
 * so executives can separate programs that BUILD capability from programs
 * that CONVERT capability into value. Same data, portfolio lens.
 */
export function ValuePathwaysTab({ programROIData }) {
  const programs = programROIData?.programs || [];
  const ranked = useMemo(
    () => [...programs]
      .filter((p) => p.conversion_efficiency != null)
      .sort((a, b) => b.conversion_efficiency - a.conversion_efficiency),
    [programs],
  );

  const maxCe = ranked.length ? Math.max(...ranked.map((p) => p.conversion_efficiency), 100) : 100;
  const tone = (ce) => (ce >= 100 ? '#10b981' : ce >= 50 ? '#f59e0b' : '#e11d48');

  return (
    <Card data-testid="value-pathways-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Workflow className="h-5 w-5 text-cyan-700" />
          Value Pathways — Capability → Value Conversion
        </CardTitle>
        <p className="text-xs text-slate-500">
          Which programs create capability versus which convert capability into value. Bars show capability → value conversion (impact ÷ investment).
        </p>
      </CardHeader>
      <CardContent>
        {ranked.length ? (
          <div className="space-y-3">
            {ranked.map((p) => {
              const ce = p.conversion_efficiency;
              const tj = TRAJECTORY_META[p.trajectory] || TRAJECTORY_META.stable;
              return (
                <div key={p.program_id} className="flex items-center gap-3" data-testid={`value-pathway-${p.program_id}`}>
                  <div className="w-48 shrink-0 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.program_name}</p>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <tj.Icon className="h-3 w-3" /> {tj.label}
                      {p.weakest_stage ? ` · constraint: ${p.weakest_stage.label}` : ''}
                    </p>
                  </div>
                  <div className="flex-1 h-6 rounded-md bg-slate-100 overflow-hidden relative">
                    <div className="h-full rounded-md flex items-center justify-end pr-2"
                      style={{ width: `${Math.min(100, (ce / maxCe) * 100)}%`, backgroundColor: tone(ce), minWidth: '2.5rem' }}>
                      <span className="text-[11px] font-bold text-white tabular-nums">{Math.round(ce)}%</span>
                    </div>
                  </div>
                  <span className="w-20 shrink-0 text-right text-xs text-slate-500 tabular-nums">
                    {p.roi_multiplier >= 0 ? '+' : ''}{p.roi_multiplier?.toFixed(2)}x ROI
                  </span>
                </div>
              );
            })}
            <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5">
              <ArrowRight className="h-3.5 w-3.5" />
              A program with strong capability but low conversion is where the Missing Middle is leaking value — the diagnostics pages explain why.
            </p>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <Workflow className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p>No conversion data yet — import program actuals to map value pathways.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
